




import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { List as IList } from 'immutable';
import { motion } from 'framer-motion';
import {
    ChatBubbleLeftIcon,
    PaperAirplaneIcon,
    XMarkIcon
} from '@heroicons/react/24/solid';

import styles from './styles';
import { getDigitalFaceplateGroups } from '../faceplate/faceplateSelectors';
import { placeOrder, validators, questions } from './ChatBotUtils';

const ChatBot = ({ faceplates }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([
        { sender: 'bot', text: 'Welcome, This is a Chatbot for placing gift card order' }
    ]);
    const [orderDetails, setOrderDetails] = useState({});
    const [step, setStep] = useState(0);
    const [input, setInput] = useState('');
    const [orderPlaced, setOrderPlaced] = useState(false);
    const messagesEndRef = useRef(null);

    const initState = () => {
        setMessages([
            { sender: 'bot', text: 'Welcome, This is a Chatbot for placing gift card order' }
        ]);
        setOrderDetails({});
        setStep(0);
        setInput('');
        setOrderPlaced(false);
    };

    useEffect(() => {
        if (orderPlaced) {
            const placeOrderAsync = async () => {
                try {
                    const orderResponse = await placeOrder(orderDetails);
                    console.log(orderResponse); 
                    const orderNumber = orderResponse.order ? orderResponse.order.order_number : 'N/A';
                    setMessages(prevMessages => [
                        ...prevMessages,
                        { sender: 'bot', text: `Order placed successfully! Your order number is ${orderNumber}.` }
                    ]);
                } catch (error) {
                    console.error('Error placing order:', error);
                    setMessages(prevMessages => [
                        ...prevMessages,
                        { sender: 'bot', text: 'Order placement failed. Please try again later.' }
                    ]);
                }
            };

            placeOrderAsync();
        }
    }, [orderPlaced]);

    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
        console.log(messages);
    }, [messages]);

    useEffect(() => {
        console.log(orderDetails);
    }, [orderDetails]);

    const handleUserResponse = async (response) => {
        if (!response.trim()) return;

        if (validators[step] && !validators[step](response, faceplates)) {
            setMessages(
                [...messages, { sender: 'user', text: response },
                    { sender: 'bot', text: 'Invalid response. Please try again.' },
                    {
                        sender: 'bot',
                        ...questions(faceplates)[step]
                    }
                ]
            );
            return;
        }

        const newMessages = [...messages, { sender: 'user', text: response }];
        const newOrderDetails = { ...orderDetails };
        const nextStep = step + 1;

        switch (step) {
            case 0:
                newMessages.push({ sender: 'bot', ...questions()[1] });
                break;
            case 1:
                newOrderDetails.type = response.toLowerCase();
                newMessages.push({ sender: 'bot', ...questions(faceplates)[2] });
                break;
            case 2:
                newOrderDetails.faceplate_code = response;
                newMessages.push({ sender: 'bot', ...questions()[3] });
                break;
            case 3:
                newOrderDetails.initial_balance = response;
                newMessages.push({ sender: 'bot', ...questions()[4] });
                break;
            case 4:
                const [recipientName, recipientEmail] = response.split(' and ');
                newOrderDetails.recipientName = recipientName;
                newOrderDetails.recipientEmail = recipientEmail;
                newMessages.push({ sender: 'bot', ...questions()[5] });
                break;
            case 5:
                const [senderName, senderEmail] = response.split(' and ');
                newOrderDetails.senderName = senderName;
                newOrderDetails.senderEmail = senderEmail;
                newMessages.push({ sender: 'bot', ...questions()[10] });
                newMessages.push({ sender: 'bot', ...questions()[6] });
                break;
            case 6:
                newOrderDetails.credit_card = { account_number: response };
                newMessages.push({ sender: 'bot', ...questions()[7] });
                break;
            case 7:
                newOrderDetails.credit_card.expiration_date = response;
                newMessages.push({ sender: 'bot', ...questions()[8] });
                break;
            case 8:
                newOrderDetails.credit_card.cvv = response;
                newMessages.push({ sender: 'bot', ...questions()[9] });
                break;
            case 9:
                newOrderDetails.credit_card.address = response;
                newMessages.push({ sender: 'bot', text: 'Thanks, We are processing your order, please stay tuned' });
                setOrderPlaced(true);
                break;
            default:
                break;
        }

        setMessages(newMessages);
        setOrderDetails(newOrderDetails);
        setStep(nextStep);
        setInput('');
    };

    return (
        <div>
            <style>
                {styles}
            </style>
            <motion.div
                className="chat-button"
                onClick={() => setIsOpen(!isOpen)}
            >
                <ChatBubbleLeftIcon className="w-6 h-6" />
            </motion.div>
            {isOpen && (
                <motion.div
                    className="chat-container"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                >
                    <div className="chat-header">
                        <span>BHN Chatbot</span>
                        <XMarkIcon
                            className="close-button"
                            onClick={() => {
                                setIsOpen(false);
                                initState();
                            }}
                        />
                    </div>
                    <div className="chat-messages">
                        {messages.map((msg, idx) => (
                            <div
                                key={idx}
                                className={`message ${
                                    msg.sender === 'bot'
                                        ? 'bot-message'
                                        : 'user-message'
                                }`}
                            >
                                {msg.text}
                                {msg.options && (
                                    <div className="option-buttons">
                                        {msg.options.map((option, i) => (
                                            <button
                                                key={i}
                                                className="option-button"
                                                type='button'
                                                onClick={() => handleUserResponse(option)}
                                            >
                                                {option}
                                            </button>
                                        ))}
                                    </div>
                                )}
                                {msg.faceplates && (
                                    <div className="image-selection">
                                        {msg.faceplates.map((fp, i) => (
                                            <img
                                                key={i}
                                                src={fp.imageURL}
                                                alt={`Design ${i + 1}`}
                                                className="gift-card-image"
                                                onClick={() => handleUserResponse(fp.code)}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                        <div ref={messagesEndRef} />
                    </div>
                    <div className="input-container">
                        <input
                            className="input-field"
                            value={input}
                            onChange={e => setInput(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && handleUserResponse(input)}
                        />
                        <button
                            className="send-button"
                            type='button'
                            onClick={() => handleUserResponse(input)}
                        >
                            <PaperAirplaneIcon className="w-5 h-5" />
                        </button>
                    </div>
                </motion.div>
            )}
        </div>
    );
};

ChatBot.propTypes = {
    faceplates: PropTypes.array.isRequired
};

const mapStateToProps = (state) => {
    const groups = getDigitalFaceplateGroups(state).first();

    return {
        faceplates: groups ? groups.get('faceplates', IList()).toJS() : []
    };
};

export default connect(mapStateToProps)(ChatBot);
